import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import getVersions from '@api/v4/attachments/versions_NEW';
import { get as getAnnotations } from '@api/v4/attachments/annotations';
import { post as postAnnotations } from '@api/v4/versions/annotations';
import Annotator from '@components/asset/modal/tabs/workflow/attachment_details/annotations/annotator';
import VersionsSidebar from '@components/asset/modal/tabs/workflow/attachment_details/versions_sidebar/main';
import CheckInCheckOut from '@components/asset/modal/tabs/workflow/attachment_details/checkin_checkout/main';
import { BFLoader } from '@components/common/loader/main';
import { processCards, updateAnnotations } from '@components/asset/modal/tabs/workflow/helper';

const AttachmentDetails = (props) => {
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsActionLoading, setVersionsActionLoading] = useState(false);
  const [showCheckinCheckout, setShowCheckinCheckout] = useState(false);
  const [activeVersion, setActiveVersion] = useState(null); // { type: "version", id: "<version id>" }
  const [versionCards, setVersionCards] = useState(null);
  const [versionCard, setVersionCard] = useState(null);
  const [users, setUsers] = useState(null);
  const [tempAnnotation, setTempAnnotation] = useState(null);
  const [annotationMode, setAnnotationMode] = useState(true);

  // stored for processing
  const [annotationsResponse, setAnnotationsResponse] = useState(null);
  const [versionsResponse, setVersionsResponse] = useState(null);

  const {
    activeAttachment,
    activeCard,
    asset,
    attachmentReservations,
    commentsFeatureEnabled,
    initializeTab,
    setAttachmentReservations,
    setAttachments,
    updateFetchControllers,
  } = props;

  const activeAttachmentReservations = attachmentReservations[activeCard.id];

  const retrieveVersions = () => (
    getVersions({ attachmentKey: activeCard.id }, updateFetchControllers)
      .then((response) => (response))
      .catch(() => {
        setVersionsLoading(false);
        // TODO handle error here
      })
  );

  const retrieveAnnotations = () => {
    const options = {
      params: { fields: 'version,parent' },
      attachmentKey: activeCard.id,
    };

    return getAnnotations(options, updateFetchControllers)
      .then((response) => (response))
      .catch(() => {
        setVersionsLoading(false);
        // TODO handle error here
      });
  };

  const resetVersionInfo = () => {
    setActiveVersion(null);
    setVersionCard(null);
    setVersionCards(null);
    setUsers(null);
  };

  const initializeSidebar = () => {
    setVersionsLoading(true);
    resetVersionInfo();

    const versionsPromise = retrieveVersions();
    const annotationsPromise = retrieveAnnotations();
    Promise.all([versionsPromise, annotationsPromise])
      .then((responses) => {
        const [versions, annotations] = responses;
        if (versions && annotations) {
          const processedCards = processCards(activeAttachmentReservations, versions, annotations);
          // these state updates are not batched because in async Promise.all
          setVersionCards(processedCards);
          setUsers({ ...versions.users, ...annotations.users, ...activeAttachmentReservations.users });
          setAnnotationsResponse(annotations);
          setVersionsResponse(versions);
        }

        setVersionsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setVersionsLoading(false);
      });
  };

  const processAnnotations = () => {
    const versionAnnotations = [];

    versionCard.annotations.forEach(({
      x_offset_in_percentage: x,
      y_offset_in_percentage: y,
      id,
      note: comment,
      type,
    }) => {
      if (type === 'annotation') {
        versionAnnotations.push({ x, y, id, comment });
      }
    });

    return versionAnnotations.reverse();
  };

  const handleNewAnnotation = (newAnnotation) => (
    postAnnotations({
      versionKey: activeVersion.id,
      note: newAnnotation.text,
      x_offset_in_percentage: newAnnotation.x,
      y_offset_in_percentage: newAnnotation.y,
      params: { fields: 'version' },
    }, updateFetchControllers)
      .then((response) => (
        setAnnotationsResponse((prevState) => (updateAnnotations(response, prevState)))
      ))
      .catch((err) => {
        console.log(err);
      })
  );

  useEffect(() => {
    if (commentsFeatureEnabled === false) {
      setAnnotationMode(false);
    }
  }, [commentsFeatureEnabled]);

  useEffect(() => {
    initializeSidebar();
  }, [activeCard]);

  useEffect(() => {
    if (!versionCards) { return undefined; }

    const activeSidebarCard = activeVersion
      ? versionCards.find((card) => card.id === activeVersion.id)
      : versionCards.find((card) => (card.type === 'version'));

    setVersionCard(activeSidebarCard);

    if (!activeVersion) {
      setActiveVersion({ type: "version", id: activeSidebarCard.id });
    }

    return undefined;
  }, [versionCards]);

  useEffect(() => {
    if (activeVersion) {
      setVersionCard(versionCards.find((card) => card.id === activeVersion.id));
    }
  }, [activeVersion]);

  useEffect(() => {
    if (!annotationsResponse || !versionsResponse || !activeAttachmentReservations) { return undefined; }

    const processedCards = processCards(activeAttachmentReservations, versionsResponse, annotationsResponse);
    setVersionCards(processedCards);
    setUsers({ ...versionsResponse.users, ...annotationsResponse.users, ...activeAttachmentReservations.users });

    return undefined;
  }, [annotationsResponse, versionsResponse, attachmentReservations]);

  return (versionsLoading || !versionCard) ? (
    <div className="loader-container">
      <BFLoader />
    </div>
  ) : (
    <section className="attachment-details">
      <Annotator
        annotationMode={annotationMode}
        annotations={processAnnotations()}
        onNewAnnotation={handleNewAnnotation}
        setTempAnnotation={setTempAnnotation}
        tempAnnotation={tempAnnotation}
        url={versionCard.version_details.large_thumbnail}
        isLoading={versionsActionLoading}
      />
      <VersionsSidebar
        activeCard={activeCard}
        activeVersion={activeVersion}
        annotationMode={annotationMode}
        activeAttachmentReservations={activeAttachmentReservations}
        commentsFeatureEnabled={commentsFeatureEnabled}
        initializeTab={initializeTab}
        setActiveVersion={setActiveVersion}
        setAnnotationMode={setAnnotationMode}
        setAnnotationsResponse={setAnnotationsResponse}
        setAttachmentReservations={setAttachmentReservations}
        setAttachments={setAttachments}
        setShowCheckinCheckout={setShowCheckinCheckout}
        setTempAnnotation={setTempAnnotation}
        setVersionsActionLoading={setVersionsActionLoading}
        updateFetchControllers={updateFetchControllers}
        users={users}
        versionCard={versionCard}
        versionCards={versionCards}
      />
      <CheckInCheckOut
        activeAttachment={activeAttachment}
        activeAttachmentReservations={activeAttachmentReservations}
        activeCard={activeCard}
        asset={asset}
        initializeTab={initializeTab}
        updateFetchControllers={updateFetchControllers}
        setAttachmentReservations={setAttachmentReservations}
        setShowCheckinCheckout={setShowCheckinCheckout}
        setVersionsActionLoading={setVersionsActionLoading}
        showCheckinCheckout={showCheckinCheckout}
        versionCard={versionCard}
      />
    </section>
  );
};

AttachmentDetails.propTypes = {
  activeAttachment: PropTypes.shape({}).isRequired,
  activeCard: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.oneOf(['asset', 'attachment'])
  }).isRequired,
  asset: PropTypes.shape({}).isRequired,
  attachmentReservations: PropTypes.shape({}).isRequired,
  commentsFeatureEnabled: PropTypes.bool.isRequired,
  initializeTab: PropTypes.func.isRequired,
  setAttachmentReservations: PropTypes.func.isRequired,
  setAttachments: PropTypes.func.isRequired,
  updateFetchControllers: PropTypes.func.isRequired,
};

export default AttachmentDetails;
